import React from "react";
import { DayPicker, Matcher, DateAfter, DateBefore } from "react-day-picker";
import "react-day-picker/style.css";
import { Box, Popover, styled } from "@mui/material";
import { startOfDay } from "date-fns";
import { DateRangeCaptionLabel } from "./DateRangeCaptionLabel";

const CalendarWrapper = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  "& .rdp-nav": {
    display: "none",
  },
  "& .rdp-root": {
    margin: theme.spacing(3.5),
    "--rdp-day-height": "42px",
    "--rdp-day-width": "42px",
    "--rdp-day_button-height": "40px",
    "--rdp-day_button-width": "40px",
    "--rdp-accent-color": theme.palette.primary.main,
    "--rdp-accent-background-color": theme.palette.background.subtle,
    "--rdp-today-color": theme.palette.text.primary,
    "--rdp-disabled-opacity": "0.25",
  },
  "& .rdp-weekday": {
    color: theme.palette.text.tertiary,
  },
  "& .rdp-today .rdp-day_button": {
    fontWeight: 700,
  },
  "& .rdp-selected": {
    fontSize: "inherit",
  },
  "& .rdp-selected .rdp-day_button": {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    border: "none",
  },
}));

export interface Props {
  className?: string;
  innerFrom?: Date;
  innerTo?: Date;
  initialMonthFrom?: Date;
  openFor: "from" | "to" | null;
  anchorEl: HTMLElement | null;
  handlePopoverClose: () => void;
  handleDaySelect: (date?: Date) => void;
  disabledMatchers: Matcher[];
}

export const DatePopover = ({
  innerFrom,
  innerTo,
  initialMonthFrom,
  openFor,
  anchorEl,
  handlePopoverClose,
  handleDaySelect,
  disabledMatchers,
}: Props) => {
  const today = startOfDay(new Date());
  const disableFuture = disabledMatchers.some(
    (matcher) => (matcher as DateAfter)?.after >= today
  );
  const disablePast = disabledMatchers.some(
    (matcher) => (matcher as DateBefore)?.before >= today
  );
  const defaultMonth =
    openFor === "from"
      ? (innerFrom ?? initialMonthFrom)
      : (innerTo ?? initialMonthFrom);

  return (
    <Popover
      open={Boolean(openFor)}
      anchorEl={anchorEl}
      onClose={handlePopoverClose}
      anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
    >
      <CalendarWrapper>
        <DayPicker
          mode="single"
          showOutsideDays
          weekStartsOn={1}
          defaultMonth={defaultMonth}
          selected={openFor === "from" ? innerFrom : innerTo}
          onSelect={handleDaySelect}
          components={{
            MonthCaption: (props) => (
              <DateRangeCaptionLabel
                {...props}
                disableFuture={disableFuture}
                disablePast={disablePast}
              />
            ),
          }}
          disabled={disabledMatchers}
        />
      </CalendarWrapper>
    </Popover>
  );
};
